import toLower from 'lodash/toLower'
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'

import { ampli } from '@Analytics/ampli'
import { PagePresentation, PageType } from '@Generated/graphql'
import { CMSModalContext, CMSModalState } from '@Globals/CMS/Modal/Context'

export const CMSModalProvider: FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<CMSModalState[0] | undefined>(undefined)

  const open = useCallback((type: PageType, id: string, sourceID?: string, presentation: PagePresentation = PagePresentation.Modal) => {
    setState((prev) => {
      if (prev && prev.type === type && prev.id === id) {
        return prev
      }

      ampli.modalOpen({
        dest_page_type_id: id,
        dest_page_type: toLower(type),
        source_id: sourceID,
      })

      return { id, presentation, type }
    })
  }, [])

  const close = useCallback(() => {
    setState((prev) => {
      if (!prev) {
        return prev
      }

      return undefined
    })
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined
    }

    const handler = (e: any) => {
      const nextModalID = e.target?.getAttribute?.('data-modal-id')
      if (!nextModalID) {
        return
      }

      e.preventDefault()
      e.stopPropagation()

      setState((prev) => {
        if (prev && prev.type === PageType.Modal && prev.id === nextModalID) {
          return prev
        }

        ampli.modalOpen({
          dest_page_type_id: nextModalID,
          dest_page_type: toLower(PageType.Modal),
          source_id: 'rte',
        })

        return {
          id: nextModalID,
          presentation: PagePresentation.Modal,
          type: PageType.Modal,
        }
      })
    }

    window.addEventListener('click', handler)

    return () => {
      window.removeEventListener('click', handler)
    }
  }, [setState])

  return (
    <CMSModalContext.Provider value={useMemo<CMSModalState>(() => [state, open, close], [close, open, state])}>
      {children}
    </CMSModalContext.Provider>
  )
}
